import { PropsWithChildren } from "react";
import { GeneralLedgerProvider } from "../../providers/GeneralLedgerProvider";
import GeneralLedgerTabs from "./GeneralLedgerTabs";
import { deepMerge } from "../../lib/utils";
import { LedgerProps } from "./types";

type GeneralLedgerProps = PropsWithChildren & LedgerProps;

export type LedgerType = "journal" | "chartofaccounts";

const defaultChartOfAccountTableStrings = {
  addAccountButtonText: "Add Account",
  nameColumnHeader: "Name",
  typeColumnHeader: "Type",
  balanceColumnHeader: "Balance",
  subtypeColumnHeader: "Sub-Type",
};

const defaultJournalTableStrings = {
  idLabel: "Entry ID",
  dateLabel: "Date",
  accountLabel: "Accounts",
  transactionLabel: "Transaction",
  debitLabel: "Debit",
  creditLabel: "Credit",
};

const defaultListingStrings = {
  dateLabel: "Date",
  journalIdLabel: "Journal ID",
  sourceLabel: "Source",
  debitLabel: "Debit",
  creditLabel: "Credit",
  runningBalanceLabel: "Running Balance",
};

const defaultJournalModalStrings = {
  modalLabel: "Transaction source",
  sourceDetails: {
    label: "Source Information",
    sourceLabel: "Source",
    recipientLabel: "Recipient Name",
    dateLabel: "Remaks",
  },
  invoice: {
    invoiceTagLabel: "Ref. Invoice",
    invoiceAmountTagLabel: "Amount",
  },
  transactionTimeLine: {
    transactionLabel: "Transaction",
    effectiveDateLabel: "Voucher CreationDate",
    creationDateLabel: "Voucher Date",
    reversalLabel: "Reversal",
  },
  journalDetails: {
    headerLabel: "Journal Details",
    accountLabel: "Account",
    debitLabel: "Debit",
    creditLabel: "Credit",
    totalLabel: "Total",
  },
  paymentDetails: {
    headerLabel: "Payment Details",
    paymentTagLabel: "Payment ID",
    totalLabel: "Total Amount",
    paymentDateLabel: "Payment Date",
    invoiceTableHeaderLabel: "Invoices",
  },
  voucherWriteOffDetails: {
    headerLabel: "Voucher Details",
    voucherIdLabel: "Voucher ID",
    totalAmountLabel: "Total Amount",
    writeOffDateLabel: "Payment Date",
  },
  invoiceList: {
    invoiceTableHeaderLabel: "Invoice List",
    invoiceIdLabel: "Invoice ID",
    invoiceAmountLabel: "Invoice Amount",
    redemptionTypeAccountLabel: "Redemption Type Account",
  },
  creditMemoList: {
    creditMemoTableHeaderLabel: "Credit Memo List",
    creditMemoIdLabel: "Credit Memo ID",
    creditMemoAmountLabel: "Amount",
    creditMemoDateLabel: "Date",
  },
  refundDetails: {
    headerLabel: "Refund Details",
    refundIdLabel: "Refund ID",
    totalAmountLabel: "Total Amount",
    refundDateLabel: "Refund Date",
  },
  // ...add more as needed
};

const GeneralLedger = ({
  children,
  withExpandAllButton = true,
  withDownloadButton = true,
  // showAddAccountButton = true,
  stringOverrides,
  ...rest
}: GeneralLedgerProps) => {
  // Merge defaults with any provided overrides
  const mergedChartOfAccountTable = {
    ...defaultChartOfAccountTableStrings,
    ...(stringOverrides?.chartOfAccountTable || {}),
  };

  const mergedJournalTable = {
    ...defaultJournalTableStrings,
    ...(stringOverrides?.journalTable || {}),
  };

  const mergedModalStrings = deepMerge(
    defaultJournalModalStrings,
    stringOverrides?.journalEntry || {}
  );

  const mergedListingStrings = {
    ...defaultListingStrings,
    ...(stringOverrides?.listing || {}),
  };

  return (
    <GeneralLedgerProvider {...rest}>
      {children}
      <GeneralLedgerTabs
        withExpandAllButton={withExpandAllButton}
        withDownloadButton={withDownloadButton}
        stringOverrides={{
          chartOfAccountTable: mergedChartOfAccountTable,
          journalTable: mergedJournalTable,
          journalEntry: mergedModalStrings,
          listing: mergedListingStrings,
        }}
      />
    </GeneralLedgerProvider>
  );
};

export { GeneralLedger };
